import { Button, Popover, Row, Col, Modal, Radio, InputNumber, message } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import './discount.style.scss';
import {
    EllipsisOutlined,
    EditOutlined
} from '@ant-design/icons';
import {
    checkActionAllowed, isValid
} from './../../utils/utilities';
import { SHOW_CLIENT_SIDE_DISCOUNT } from "../../utils/constant";
import { getValueFromPercentage, roundNumber } from "../../utils/math.utils";
import { maxDiscountConfigSelector } from "../../selector/passenger-form.selector";

// Max discount for the agent's privilege comes from the seats api
// (operator_discount_privilege_mapping). No config means no restriction.
// Flat limit is per seat, so the ticket-level cap is maxValue * seatCount.
const validateMaxDiscount = (discountAmount, baseFare, maxDiscountConfig, seatCount) => {
    if (!isValid(maxDiscountConfig) || !isValid(maxDiscountConfig.max_client_discount_value)) {
        return true;
    }
    const maxValue = Number(maxDiscountConfig.max_client_discount_value);
    const isPercentage = maxDiscountConfig.is_percentage == 1;
    const noOfSeats = seatCount > 0 ? seatCount : 1;
    const maxAmount = isPercentage ? roundNumber(getValueFromPercentage(baseFare, maxValue)) : roundNumber(maxValue * noOfSeats);
    if (discountAmount > maxAmount) {
        const limitStr = isPercentage
            ? `${maxValue}% (₹${maxAmount})`
            : (noOfSeats > 1 ? `₹${maxValue} per seat (₹${maxAmount} for ${noOfSeats} seats)` : `₹${maxValue} per seat`);
        message.warning(`Discount cannot exceed the maximum allowed ${limitStr} for your privilege.`);
        return false;
    }
    return true;
};

const DiscountModal = ({ discountValue, handleCancel, handleApply, discountType, setDiscountType }) => {
    const [inputValue, setInputValue] = useState(discountValue || 0);

    const handleApplyClick = () => {
        handleApply(inputValue);
    };

    return (
        <div className="discount-modal-root">
            <div>
                <Row>
                    <Col>
                        <div className='fontWeightBold'>
                            Discount
                        </div>
                    </Col>
                </Row>
                <Radio.Group
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    style={{ marginBottom: '16px' }}
                >
                    <Radio value="amount">Amount</Radio>
                    <Radio value="percentage">Percentage</Radio>
                </Radio.Group>
                <br />
                <InputNumber
                    value={inputValue}
                    onChange={(value) => setInputValue(value)}
                    style={{ marginRight: '8px' }}
                />
                {discountType === 'amount' ? " ₹" : " %"}
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col>
                        <Button className="exit-btn" onClick={handleCancel}>Cancel</Button>
                    </Col>
                    <Col>
                        <Button type="primary" danger onClick={handleApplyClick}>Apply</Button>
                    </Col>
                </Row>
            </div>
        </div>
    )
}

const AddDiscountComponent = (props) => {
    const {
        showModal,
        discountValue,
        removeDisCount
    } = props;

    if (!isValid(discountValue)) {
        return (
            <div className="add-button-content">
                <Button type="primary" danger onClick={showModal}>Add Discount</Button>
            </div>
        )
    } else {
        return (
            <div className="add-button-content">
                <Button onClick={removeDisCount}>Remove Discount</Button>
            </div>
        )
    }
}

const EditDiscountComponent = (props) => {
    const {
        showModal
    } = props;

    return (
        <div className="add-button-content">
            <Button onClick={showModal}>Edit Discount</Button>
        </div>
    )
}

export const AddDiscount = (props) => {
    const {
        baseFare,
        discountValue,
        setDiscountValue,
        seatCount
    } = props;
    
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showAddButton, setShowAddButton] = useState(false);
    const [discountType, setDiscountType] = useState('amount');
    const maxDiscountConfig = useSelector(maxDiscountConfigSelector);

    const showModal = () => {
        setIsModalVisible(true);
        setShowAddButton(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setShowAddButton(false);
    }

    const handleApply = (value) => {
        const discountAmount = discountType === 'amount' ? value : roundNumber(getValueFromPercentage(baseFare, value));
        if(!validateMaxDiscount(discountAmount, baseFare, maxDiscountConfig, seatCount)){
            return;
        }
        setDiscountValue(discountAmount);
        handleCancel();
    }

    const removeDisCount = () => {
        setDiscountValue(0);
        handleCancel();
    }

    if (checkActionAllowed(SHOW_CLIENT_SIDE_DISCOUNT)) {
        return (
            <div className="discount-container">
                <Popover 
                 visible={showAddButton}
                 onVisibleChange={(visible) => setShowAddButton(visible)}
                 trigger="click"    
                >
                    <Button
                        type="text"
                        className="outline-button three-dots-button"
                        icon={<EllipsisOutlined rotate={90} style={{ fontSize: '20px' }} />}
                    />
                    {showAddButton && (
                        <div className="add-discount-popup">
                            <AddDiscountComponent
                                showModal={showModal}
                                discountValue={discountValue}
                                removeDisCount={removeDisCount}
                            />
                        </div>
                    )}
                </Popover>
                <Modal
                    visible={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    title={"Discount"}
                    width={400}
                >
                    <DiscountModal
                        discountValue={discountValue}
                        handleCancel={handleCancel}
                        handleApply={handleApply}
                        discountType={discountType}
                        setDiscountType={setDiscountType}
                    />
                </Modal>
            </div>
        );
    } else {
        return null;
    }
}

export const EditDiscount = (props) => {
    const {
        discountValue,
        setDiscountValue,
        baseFare,
        seatCount
    } = props;

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showEditButton, setShowEditButton] = useState(false);
    const [discountType, setDiscountType] = useState('amount');
    const maxDiscountConfig = useSelector(maxDiscountConfigSelector);

    const showModal = () => {
        setIsModalVisible(true);
        setShowEditButton(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setShowEditButton(false);
    }

    const handleApply = (value) => {
        const discountAmount = discountType === 'amount' ? value : roundNumber(getValueFromPercentage(baseFare, value));
        if(!validateMaxDiscount(discountAmount, baseFare, maxDiscountConfig, seatCount)){
            return;
        }
        setDiscountValue(discountAmount);
        handleCancel();
    }

    if (checkActionAllowed(SHOW_CLIENT_SIDE_DISCOUNT)) {
        return (
            <div className="discount-container">
                <Popover 
                 visible={showEditButton}
                 onVisibleChange={(visible) => setShowEditButton(visible)}
                 trigger="click"    
                >
                    <Button
                        type="text"
                        className="outline-button three-dots-button"
                        icon={<EditOutlined rotate={90}  style={{ fontSize: '20px' }} />}
                    />
                    {showEditButton && (
                        <div className="add-discount-popup">
                            <EditDiscountComponent showModal={showModal} />
                        </div>
                    )}
                </Popover>
                <Modal
                    visible={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    title={"Discount"}
                    width={400}
                >
                    <DiscountModal
                        discountValue={discountValue}
                        handleCancel={handleCancel}
                        handleApply={handleApply}
                        discountType={discountType}
                        setDiscountType={setDiscountType}
                    />
                </Modal>
            </div>
        )
    } else {
        return null;
    }
}